import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FileText, Loader2, Eye, Target, Table2, Calculator, CalendarRange, Palette } from "lucide-react";
import SwotBoard from "../components/SwotBoard";
import CompetitorTracker from "../components/CompetitorTracker";
import PricingCalculator from "../components/PricingCalculator";
import ActionPlan from "../components/ActionPlan";
import BrandKit from "../components/BrandKit";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import api from "../lib/apiClient";
import { exportPlanPDF } from "../lib/pdf";
import PolicyConsentDialog from "../components/PolicyConsentDialog";

const POLICY_LS_KEY = "swotkit_policy_accepted";

function Section({ icon: Icon, title, children }) {
  return (
    <section className="mt-10">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-5 w-5 text-indigo-400" />
        <h2 className="font-display text-xl font-bold">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export default function SharedView() {
  const { token } = useParams();
  const [plan, setPlan] = useState(null);
  const [state, setState] = useState("loading");
  const [showPolicy, setShowPolicy] = useState(false);

  const exportPdf = () => {
    if (localStorage.getItem(POLICY_LS_KEY) === "true") { exportPlanPDF(plan); return; }
    setShowPolicy(true);
  };

  const onPolicyAccept = () => {
    localStorage.setItem(POLICY_LS_KEY, "true");
    setShowPolicy(false);
    exportPlanPDF(plan);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/shared/${token}`);
        setPlan(res.data);
        setState("ok");
      } catch (e) {
        setState("error");
      }
    })();
  }, [token]);

  if (state === "loading") {
    return <div className="min-h-screen grid-bg flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-indigo-400" /></div>;
  }
  if (state === "error") {
    return (
      <div className="min-h-screen grid-bg flex flex-col items-center justify-center text-slate-300 gap-3">
        <Eye className="h-10 w-10 text-slate-600" />
        <p className="font-display text-xl">This shared plan is unavailable.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid-bg text-slate-100">
      <nav className="glass sticky top-0 z-50 border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display font-extrabold text-lg">
          <img src="/swotkit-logo.png" alt="SwotKit" className="h-9 w-9 rounded-lg bg-white p-0.5" />
          SwotKit
        </div>
        <Button data-testid="shared-pdf-button" size="sm" onClick={exportPdf} className="cta-btn">
          <FileText className="h-4 w-4 mr-1" /> Export PDF
        </Button>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <Badge className="bg-teal-500/15 text-teal-300 mb-3"><Eye className="h-3 w-3 mr-1" /> Read-only shared view</Badge>
        <div className="flex items-start gap-4">
          {plan.logo && <img data-testid="shared-logo" src={plan.logo} alt="logo" className="h-16 w-16 rounded-2xl object-cover border border-slate-700 bg-white shrink-0" />}
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold">{plan.business_name}</h1>
            {plan.industry && <p className="text-slate-400 mt-1">{plan.industry}</p>}
          </div>
        </div>
        {plan.description && <p className="text-slate-300 mt-3 max-w-2xl leading-relaxed">{plan.description}</p>}

        <Section icon={Target} title="SWOT Analysis">
          <SwotBoard plan={plan} value={plan.swot} onChange={() => {}} readOnly />
        </Section>
        <Section icon={Table2} title="Competitor Tracker">
          <CompetitorTracker plan={plan} value={plan.competitors} onChange={() => {}} readOnly />
        </Section>
        <Section icon={Calculator} title="Pricing Calculator">
          <PricingCalculator plan={plan} value={plan.pricing} onChange={() => {}} readOnly />
        </Section>
        <Section icon={CalendarRange} title="90-Day Action Plan">
          <ActionPlan plan={plan} value={plan.action_plan} onChange={() => {}} readOnly />
        </Section>
        {plan.brand_kit && plan.brand_kit.colors && plan.brand_kit.colors.length > 0 && (
          <Section icon={Palette} title="Brand Kit">
            <BrandKit plan={plan} value={plan.brand_kit} readOnly />
          </Section>
        )}
      </div>

      <PolicyConsentDialog open={showPolicy} onOpenChange={setShowPolicy} onAccept={onPolicyAccept} actionLabel="Agree & Download" />
    </div>
  );
}
